/* eslint-disable @typescript-eslint/no-explicit-any, max-params */
import invariant from "tiny-invariant";
import { Asset } from "../../models/financial/asset.js";
/**
 * A currency is any fungible financial instrument, including Ether, all ERC20 tokens, and other chain-native currencies
 */
export class Currency extends Asset {
    /**
     * Constructs an instance of the base class `Currency`.
     * @param decimals decimals of the currency
     * @param symbol symbol of the currency
     * @param name of the currency
     * @param logoUrl URL for the currency logo
     * @param isNative whether the currency is native to the chain and must be wrapped (e.g. Ether)
     * @param isToken whether the currency is a token that is usable in Uniswap without wrapping
     * @param coinGeckoId CoinGecko ID for price lookups
     * @param isStablecoin whether the currency is a stablecoin
     */
    constructor(decimals, symbol, name, logoUrl, isNative, isToken, coinGeckoId, isStablecoin) {
        super(symbol, name, coinGeckoId, isStablecoin);
        invariant(decimals >= 0 && decimals < 255 && Number.isInteger(decimals), "DECIMALS");
        this.decimals = decimals;
        this.isNative = isNative ?? false;
        this.isToken = isToken ?? true;
        this.logoUrl = logoUrl;
    }
    /**
     * Returns whether this currency is functionally equivalent to the other currency
     * @param other the other currency
     */
    equals(other) {
        if (this === other)
            return true;
        if (this.coinGeckoId && other.coinGeckoId && this.coinGeckoId === other.coinGeckoId)
            return true;
        return this.symbol === other.symbol && this.name === other.name && this.decimals === other.decimals;
    }
    /**
     * Returns the asset representation of this currency
     */
    asset() {
        return this;
    }
    /**
     * Converts the currency to a model representation for serialization
     */
    toCurrencyModel() {
        return {
            asset: this.toAssetModel(),
            decimals: this.decimals,
            logoUrl: this.logoUrl,
            isNative: this.isNative,
            isToken: this.isToken,
            coinGeckoId: this.coinGeckoId,
            isStablecoin: this.isStablecoin,
        };
    }
    static toCurrency(currency) {
        if (!currency) {
            return undefined;
        }
        const { asset, decimals, logoUrl, isNative, isToken, coinGeckoId, isStablecoin } = currency;
        return new Currency(decimals, asset.symbol, asset.name, logoUrl, isNative, isToken, coinGeckoId, isStablecoin);
    }
    identifier() {
        return Currency.createIdentifier(this.name, this.symbol);
    }
    static createIdentifier(name, symbol) {
        return `${name}:${symbol}`;
    }
}
